import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import {
  FinancialData,
  LeasingBrowseResponseItem,
  LeasingEquipmentResp,
  LeasingEquipmentTabData,
  LeasingEquipmentUpdateData,
  LeasingLeaseAuditTabData,
  LeasingSummaryTabData,
  LeasingWorkflowTabData,
  LeasingWorkflowUpdateData,
  MainBrowseFilter,
  PaginatedResponse,
  PermittingJurisdictionAuditData,
  RadData,
} from '@models/interfaces';
import { LeasingLeaseAuditUpdateData } from '@models/types';
import {
  BreadcrumbsActions,
  FinancialActions,
  LeasingActions,
  PermittingActions,
  ProjectActions,
} from '@store/actions';
import { LeasingEffects } from '@store/effects';
import store from '@store/index';
import { CommonHttpService } from './common-http.service';

export class LeasingHttpService extends CommonHttpService {
  @StoredRequest(LeasingActions.getLeasingBrowseDataAction)
  getLeasingBrowse(
    params: MainBrowseFilter,
  ): Promise<PaginatedResponse<LeasingBrowseResponseItem>> {
    return this.http.callApi.get(`${this.apiUrl}/leasing`, null, {
      params,
    });
  }

  @StoredRequest(
    LeasingActions.getSummaryTabDataAction,
    BreadcrumbsActions.leasing,
  )
  async getLeasingSummaryData(
    projectBundleID: string,
  ): Promise<LeasingSummaryTabData> {
    const response: LeasingSummaryTabData = await this.http.callApi.get(
      `${this.apiUrl}/leasing/${projectBundleID}/summary`,
    );

    LeasingEffects.handleLeasingSummary(response);

    return response;
  }

  @StoredRequest(LeasingActions.getWorkflowTabDataAction)
  getLeasingWorkflowData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<LeasingWorkflowTabData> {
    return this.http.callApi.get(
      `${this.apiUrl}/leasing/${projectBundleID}/workflow`,
    );
  }

  @StoredRequest(LeasingActions.getLeaseAuditTabDataAction)
  async getLeasingLeaseAuditData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<LeasingLeaseAuditTabData> {
    const response: LeasingLeaseAuditTabData = await this.http.callApi.get(
      `${this.apiUrl}/leasing/${projectBundleID}/lease-audit`,
    );

    LeasingEffects.handleLeasingLeaseAudit(response);

    return response;
  }

  @StoredRequest(LeasingActions.getEquipmentTabDataAction)
  getLeasingEquipmentTabData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<LeasingEquipmentTabData> {
    return this.http.callApi.get(
      `${this.apiUrl}/leasing/${projectBundleID}/equipment-data`,
    );
  }

  @StoredRequest(
    LeasingActions.getJurisdictionAuditTabData,
    PermittingActions.getJurisdictionAuditTabData,
  )
  async getLeasingJurisdictionAuditData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<PermittingJurisdictionAuditData> {
    const response: PermittingJurisdictionAuditData =
      await this.http.callApi.get(
        `${this.apiUrl}/permitting/${projectBundleID}/jurisdiction-audit`,
      );

    return response;
  }

  @StoredRequest(
    FinancialActions.getFinancialDataAction,
    PermittingActions.getFinancialTabData,
    LeasingActions.getFinancialTabData,
  )
  async getPermittingFinancialData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<FinancialData> {
    const response: FinancialData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/financial`,
    );

    return response;
  }

  @StoredRequest(LeasingActions.getLeasingEquipmentPriorProjectAction)
  getLeasingEquipmentPriorProject(id: string): Promise<LeasingEquipmentResp> {
    return this.http.callApi.get(`${this.apiUrl}/leasing/${id}/equipment`);
  }

  @StoredRequest(LeasingActions.updateLeasingWorkflowAction)
  async updateLeasingWorkflow(
    projectId: string,
    body: LeasingWorkflowUpdateData,
  ): Promise<LeasingWorkflowUpdateData> {
    await this.http.callApi.patch(`${this.apiUrl}/leasing/${projectId}`, body);

    return body;
  }

  @StoredRequest(LeasingActions.updateLeaseAuditAction)
  async updateLeaseAudit(
    projectId: string,
    area: 'engineering' | 'leasing',
    body: Partial<LeasingLeaseAuditUpdateData> & { RAD: RadData[] },
  ): Promise<
    Partial<LeasingLeaseAuditUpdateData> & { area: 'engineering' | 'leasing' }
  > {
    await this.http.callApi.patch(
      `${this.apiUrl}/leasing/lease-audit/${projectId}`,
      body,
    );

    const { RAD, ...rest } = body;

    store.dispatch(ProjectActions.setRADAction.done(RAD));

    return { ...rest, area };
  }

  @StoredRequest(LeasingActions.updateLeasingEquipmentAction)
  async updateLeasingEquipment(
    id: string,
    { priorTableData, finalTableData, ...notes }: LeasingEquipmentUpdateData,
  ): Promise<LeasingEquipmentUpdateData> {
    await this.http.callApi.patch(`${this.apiUrl}/leasing/${id}/equipment`, {
      /**
       * INFO: because backend does not need to equipment Type, but leasing-reducer does need
       * and wait equipmentId.
       */
      priorTableData: priorTableData.map(
        ({ equipmentType, equipment, ...data }) => ({
          ...data,
          equipmentId: equipment,
        }),
      ),
      finalTableData: finalTableData.map(
        ({ equipmentType, equipment, ...data }) => ({
          ...data,
          equipmentId: equipment,
        }),
      ),
      ...notes,
    });

    return { priorTableData, finalTableData, ...notes };
  }
}
